import { fetchWithAuth } from '../lib/fetchWithAuth';

const API_BASE_URL = import.meta.env.VITE_API_URL?.replace(/\/$/, '');

export type FeatureFlag = {
  key: string;
  label?: string;
  description?: string;
  enabled: boolean;
};

export type AdminSettings = {
  maintenanceMode: boolean;
  maintenanceMessage: string;
  featureFlags: FeatureFlag[];
};

export async function fetchAdminSettings(): Promise<AdminSettings> {
  if (!API_BASE_URL) {
    throw new Error('VITE_API_URL is required to load admin settings.');
  }
  const response = await fetchWithAuth(`${API_BASE_URL}/v1/admin/settings`);
  if (!response.ok) {
    const text = await response.text().catch(() => 'request failed');
    throw new Error(`Admin settings fetch failed (${response.status}): ${text}`);
  }
  return (await response.json()) as AdminSettings;
}

export async function updateMaintenanceMode(maintenanceMode: boolean, maintenanceMessage?: string): Promise<AdminSettings> {
  if (!API_BASE_URL) {
    throw new Error('VITE_API_URL is required to update maintenance mode.');
  }
  const response = await fetchWithAuth(`${API_BASE_URL}/v1/admin/settings/maintenance`, {
    method: 'PATCH',
    body: JSON.stringify({ maintenanceMode, maintenanceMessage }),
  });
  if (!response.ok) {
    const text = await response.text().catch(() => 'request failed');
    throw new Error(`Maintenance update failed (${response.status}): ${text}`);
  }
  return (await response.json()) as AdminSettings;
}

export async function updateFeatureFlag(flag: FeatureFlag): Promise<AdminSettings> {
  if (!API_BASE_URL) {
    throw new Error('VITE_API_URL is required to update feature flags.');
  }
  const response = await fetchWithAuth(`${API_BASE_URL}/v1/admin/settings/flags/${flag.key}`, {
    method: 'PATCH',
    body: JSON.stringify(flag),
  });
  if (!response.ok) {
    const text = await response.text().catch(() => 'request failed');
    throw new Error(`Feature flag update failed (${response.status}): ${text}`);
  }
  return (await response.json()) as AdminSettings;
}
